import { HttpClient, RouteGenerator } from "silver";
import { Book } from "core/Data/Book";
import { MyonApiResponse, isMyonApiSuccess } from "shared/Api/MyonApiResponse";

export type BookSearchField = "title"|"author"|"series"|"publisher"|"description";
export type BookSearchLength = "any"|"short"|"medium"|"long"|"epic";

export interface BookSearchQuery {
	search?: BookSearchField[];
	language?: string;
	length?: BookSearchLength;
	guidedReading?: [string, string];
	gradeLevel?: [number, number];
	lexile?: [number, number];
	atos?: [ number, number ];
	fiction?: boolean;
	audio?: 1|0;
	publisher?: number;
	series?: number;
	text?: string;
}

interface SeriesSearchQuery {
	series: number;
}

interface AuthorSearchQuery {
	author: string;
}

interface PublisherSearchQuery {
	publisher: number;
}

interface LibraryAdvancedSearchQuery {
	q?: string;
	fiction?: boolean;
	lexile?: [number, number];
	guidedReading?: [string, string];
	gradeLevel?: [number, number];
	language?: string;
	length?: BookSearchLength;
	atos?: [ number, number ];
	publisher?: number;
	audio?: 1|0;
}

export type SearchBooksAutoCompleteResult = {
	type: "book"|"series"|"author"|"category"|"publisher"
	similarity: number;
	title: string;
	url: string;
};

type LibrarySearchQuery = SeriesSearchQuery | AuthorSearchQuery | PublisherSearchQuery | LibraryAdvancedSearchQuery;

export class SearchBooksService {
	constructor(
		private router: RouteGenerator,
		private httpClient: HttpClient
	) { }

	public async search(params: BookSearchQuery): Promise<number[]> {
		const url = this.router.generate("api\\books", "search", params as any, "json");
		const response: MyonApiResponse<number[]> = await this.httpClient.get(url);
		if (! isMyonApiSuccess(response)) {
			console.error(response);
			throw new Error("Search failed");
		}
		return response.result;
	}

	public async autocomplete(text: string, abortController?: AbortController): Promise<SearchBooksAutoCompleteResult[]> {
		text = text.replace(/[+_\-><()~*@]+/, " ");
		const url = this.router.generate("api\\books", "autocomplete", { text }, "json");
		const response: MyonApiResponse<SearchBooksAutoCompleteResult[]> = await this.httpClient.get(url, { abortController });
		if (!isMyonApiSuccess(response)) {
			console.error(response);
			throw new Error("Autocomplete search failed");
		}
		return response.result;
	}

	public getSearchURL(params: LibrarySearchQuery): string {
		return this.router.generate("library\\search", null, params as any);
	}

	public getSimilarLexileSearchURL(book: Book): string|null {
		if (! book.lexileLevel) {
			return null;
		}
		return this.getSearchURL({ lexile: [ book.lexileLevel - 50, book.lexileLevel + 100 ] });
	}

	public getGradeLevelSearchURL(book: Book): string|null {
		if (!book.minUserGradeLevelId || !book.maxUserGradeLevelId) {
			return null;
		}
		return this.getSearchURL({ gradeLevel: [ book.minUserGradeLevelId, book.maxUserGradeLevelId ] });
	}

	public getBookPublisherSearchURL(book: Book): string|null {
		if (!book.bookPublisherTitle) {
			return null;
		}
		return this.getSearchURL({ publisher: book.bookPublisherId });
	}

	public getBookSeriesSearchURL(book: Book): string|null {
		if (!book.bookSeriesTitle) {
			return null;
		}
		return this.getSearchURL({ series: book.bookSeriesId });
	}

	public getBookGuidedReadingLevelSearchURL(book: Book): string|null {
		if (!book.guidedReadingLevel) {
			return null;
		}
		return this.getSearchURL({ guidedReading: [
			book.guidedReadingLevel,
			String.fromCharCode(book.guidedReadingLevel.charCodeAt(0) + 1)
		] });
	}

	public getBookAudioRuntimeSearchURL(book: Book): string|null {
		if (!book.audioRuntimeFriendly) {
			return null;
		}
		return this.getSearchURL({
			audio:  book.audioRuntimeSecs ? 1 : 0,
			length: (!book.audioRuntimeSecs ? undefined :
				((book.audioRuntimeSecs <= 15 * 60) ? "short" :
					((book.audioRuntimeSecs <= 30 * 60) ? "medium" :
						((book.audioRuntimeSecs <= 60 * 60) ? "long" :
							"epic"))))
		});
	}

	public getBookPageCountSearchURL(book: Book): string|null {
		if (!book.pagecount) {
			return null;
		}
		return this.getSearchURL({
			length: (book.pagecount <= 24) ? "short" :
				((book.pagecount <= 40) ? "medium" :
					((book.pagecount <= 100) ? "long" :
						"epic"))
		});
	}

	public getSimilarATOSSearchURL(book: Book): string|null {
		if (! book.arQuizLevel) {
			return null;
		}
		return this.getSearchURL({ atos: [ ((book.arQuizLevel) - 0.2), ((book.arQuizLevel) + 0.2) ] });
	}
}
